import { useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import type { DictionaryMode } from "contexts/DictionaryToolsContext";
import {
  addItemToPreviousSearch,
  searchDictionary,
  useAddSavedWord,
} from "services/dictionary";

// Owns the sidebar's search input, active tab, and the define/translate/save lookups.
export function useDictionarySearch() {
  const {
    mutate: runSearch,
    data: searchResult,
    isError,
    isPending,
    variables: lastSearch,
  } = useMutation({ mutationFn: searchDictionary });
  const { mutate: runAddWord } = useAddSavedWord();

  const [text, setText] = useState("");
  const [mode, setMode] = useState<DictionaryMode>("define");
  const [myWordsSearchText, setMyWordsSearchText] = useState("");

  const translate = useCallback((selection: string) => {
    setText(selection);
    setMode("translate");
  }, []);

  const define = useCallback(
    (selection: string) => {
      setText(selection);
      setMode("define");
      addItemToPreviousSearch(selection);
      runSearch({ text: selection });
    },
    [runSearch]
  );

  const saveWord = useCallback(
    (selection: string) => {
      setText(selection);
      setMode("save");
      runAddWord({ word: selection });
    },
    [runAddWord]
  );

  const applyLookup = useCallback(
    (lookupMode: DictionaryMode, selection: string) => {
      if (lookupMode === "translate") {
        translate(selection);
      } else if (lookupMode === "define") {
        define(selection);
      } else {
        saveWord(selection);
      }
    },
    [translate, define, saveWord]
  );

  const onSearch = (searchInput?: string) => {
    if (mode === "save") {
      // In the My Vocab tab, Enter filters the saved list instead of searching the dictionary.
      setMyWordsSearchText(searchInput ?? text);
      return;
    }
    const searchText = searchInput || text;
    if (!searchText) {
      return;
    }
    define(searchText);
  };

  const onModeChanged = (newMode: DictionaryMode) => {
    // Switching to Define auto-looks-up the box, but skip a word we already fetched
    if (newMode === "define" && text && text !== lastSearch?.text) {
      define(text);
    } else {
      setMode(newMode);
    }
  };

  const clearMyWordsFilter = useCallback(() => setMyWordsSearchText(""), []);

  return {
    text,
    setText,
    mode,
    onModeChanged,
    onSearch,
    applyLookup,
    myWordsSearchText,
    clearMyWordsFilter,
    result: searchResult ?? null,
    isError,
    isFetching: isPending,
  };
}
